import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/components/auth-provider";
import ProtectedRoute from "@/components/protected-route";
import { LocaleDocument } from "@/components/locale-document";
import { RoutePageview } from "@/components/route-pageview";
import { CookieConsent } from "@/components/cookie-consent";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Features from "@/pages/Features";
import Pricing from "@/pages/Pricing";
import Auth from "@/pages/Auth";
import Integrations from "@/pages/Integrations";
import Changelog from "@/pages/Changelog";
import About from "@/pages/About";
import Customers from "@/pages/Customers";
import Careers from "@/pages/Careers";
import Press from "@/pages/Press";
import Help from "@/pages/Help";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import Community from "@/pages/Community";
import Contact from "@/pages/Contact";
import Security from "@/pages/Security";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";

import Dashboard from "@/pages/app/Dashboard";
import Clients from "@/pages/app/Clients";
import Appointments from "@/pages/app/Appointments";
import Notes from "@/pages/app/Notes";
import Billing from "@/pages/app/Billing";
import CarePlans from "@/pages/app/CarePlans";
import CarePlanTemplates from "@/pages/app/CarePlanTemplates";
import Settings from "@/pages/app/Settings";
import HrDashboard from "@/pages/app/hr/HrDashboard";
import HrEmployees from "@/pages/app/hr/HrEmployees";
import HrEmployeeDetail from "@/pages/app/hr/HrEmployeeDetail";
import HrContracts from "@/pages/app/hr/HrContracts";
import HrLeave from "@/pages/app/hr/HrLeave";
import HrTimesheets from "@/pages/app/hr/HrTimesheets";
import HrPayroll from "@/pages/app/hr/HrPayroll";
import HrApp from "@/pages/app/hr/HrApp";
import HrOnboarding from "@/pages/app/hr/HrOnboarding";
import Book from "@/pages/Book";
import PayInvoice from "@/pages/PayInvoice";
import PatientPortal from "@/pages/PatientPortal";
import PatientVideoCall from "@/pages/PatientVideoCall";
import Patient360 from "@/pages/app/Patient360";
import Waitlist from "@/pages/app/Waitlist";
import Analytics from "@/pages/app/Analytics";
import Communications from "@/pages/app/Communications";
import Referrals from "@/pages/app/Referrals";
import SchedulingResources from "@/pages/app/SchedulingResources";
import Queues from "@/pages/app/Queues";
import QueueDisplay from "@/pages/app/QueueDisplay";
import Workflows from "@/pages/app/Workflows";
import Network from "@/pages/app/Network";
import Adt from "@/pages/app/Adt";
import Campaigns from "@/pages/app/Campaigns";
import IntegrationsApp from "@/pages/app/IntegrationsApp";
import ReminderRules from "@/pages/app/ReminderRules";
import VideoCall from "@/pages/app/VideoCall";

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <LocaleDocument />
      <BrowserRouter>
        <RoutePageview />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/about" element={<About />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/press" element={<Press />} />
            <Route path="/help" element={<Help />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/community" element={<Community />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/security" element={<Security />} />
            <Route path="/legal/:doc" element={<LegalPage />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/signup" element={<Auth mode="signup" />} />
            <Route path="/book/:slug" element={<Book />} />
            <Route path="/book" element={<Book />} />
            <Route path="/pay/:token" element={<PayInvoice />} />
            <Route path="/patient" element={<PatientPortal />} />
            <Route path="/patient/call" element={<PatientVideoCall />} />

            <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/app/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
            <Route path="/app/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
            <Route path="/app/call/:appointmentId" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
            <Route path="/app/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
            <Route path="/app/care-plans" element={<ProtectedRoute><CarePlans /></ProtectedRoute>} />
            <Route path="/app/care-plan-templates" element={<ProtectedRoute><CarePlanTemplates /></ProtectedRoute>} />
            <Route path="/app/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/app/hr" element={<ProtectedRoute><HrDashboard /></ProtectedRoute>} />
            <Route path="/app/hr/employees" element={<ProtectedRoute><HrEmployees /></ProtectedRoute>} />
            <Route path="/app/hr/employees/:id" element={<ProtectedRoute><HrEmployeeDetail /></ProtectedRoute>} />
            <Route path="/app/hr/contracts" element={<ProtectedRoute><HrContracts /></ProtectedRoute>} />
            <Route path="/app/hr/leave" element={<ProtectedRoute><HrLeave /></ProtectedRoute>} />
            <Route path="/app/hr/timesheets" element={<ProtectedRoute><HrTimesheets /></ProtectedRoute>} />
            <Route path="/app/hr/payroll" element={<ProtectedRoute><HrPayroll /></ProtectedRoute>} />
            <Route path="/app/hr/app" element={<ProtectedRoute><HrApp /></ProtectedRoute>} />
            <Route path="/app/hr/onboarding" element={<ProtectedRoute><HrOnboarding /></ProtectedRoute>} />
            <Route path="/app/patients/:id" element={<ProtectedRoute><Patient360 /></ProtectedRoute>} />
            <Route path="/app/waitlist" element={<ProtectedRoute><Waitlist /></ProtectedRoute>} />
            <Route path="/app/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/app/communications" element={<ProtectedRoute><Communications /></ProtectedRoute>} />
            <Route path="/app/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
            <Route path="/app/scheduling/resources" element={<ProtectedRoute><SchedulingResources /></ProtectedRoute>} />
            <Route path="/app/queues" element={<ProtectedRoute><Queues /></ProtectedRoute>} />
            <Route path="/app/queues/display" element={<ProtectedRoute><QueueDisplay /></ProtectedRoute>} />
            <Route path="/app/workflows" element={<ProtectedRoute><Workflows /></ProtectedRoute>} />
            <Route path="/app/network" element={<ProtectedRoute><Network /></ProtectedRoute>} />
            <Route path="/app/adt" element={<ProtectedRoute><Adt /></ProtectedRoute>} />
            <Route path="/app/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
            <Route path="/app/integrations" element={<ProtectedRoute><IntegrationsApp /></ProtectedRoute>} />
            <Route path="/app/reminder-rules" element={<ProtectedRoute><ReminderRules /></ProtectedRoute>} />

            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
        <CookieConsent />
        <Toaster />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
